<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useSearchIndex, type SearchResult } from '@shared/composables/useSearchIndex'
import { getModuleIcon } from '@shared/ui/icons'

const router = useRouter()
const { results, query, search, buildIndex } = useSearchIndex()

const isOpen = ref(false)
const selectedIndex = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)

function open() {
  isOpen.value = true
  selectedIndex.value = 0
  query.value = ''
  results.value = []
  buildIndex()
  nextTick(() => inputRef.value?.focus())
}

function close() {
  isOpen.value = false
}

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    isOpen.value ? close() : open()
    return
  }
  if (!isOpen.value) return

  switch (e.key) {
    case 'Escape':
      e.preventDefault()
      close()
      break
    case 'ArrowDown':
      e.preventDefault()
      selectedIndex.value = Math.min(selectedIndex.value + 1, results.value.length - 1)
      break
    case 'ArrowUp':
      e.preventDefault()
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
      break
    case 'Enter':
      e.preventDefault()
      selectResult(results.value[selectedIndex.value])
      break
  }
}

function selectResult(item: SearchResult | undefined) {
  if (!item) return
  close()
  router.push(item.route)
}

function onInput(e: Event) {
  const val = (e.target as HTMLInputElement).value
  selectedIndex.value = 0
  search(val)
}

watch(results, () => {
  if (selectedIndex.value >= results.value.length) {
    selectedIndex.value = Math.max(0, results.value.length - 1)
  }
})

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <!-- Trigger button (to be placed in app header) -->
  <button
    class="flex items-center gap-xs text-text-secondary text-sm bg-background-secondary px-sm py-xs rounded-md border border-border-subtle hover:border-brand-primary transition-colors"
    @click="open"
    title="搜索 (Cmd+K)"
  >
    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
    <span class="hidden sm:inline">搜索</span>
    <kbd class="hidden sm:inline text-xs bg-background-primary px-xs rounded border border-border-subtle ml-xs">⌘K</kbd>
  </button>

  <!-- Modal overlay -->
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      @click.self="close"
    >
      <div class="absolute inset-0 bg-black/40" />
      <div class="relative w-full max-w-lg bg-background-secondary border border-border-subtle rounded-lg shadow-lg overflow-hidden">
        <!-- Input -->
        <div class="flex items-center gap-sm px-md py-sm border-b border-border-subtle">
          <svg class="w-5 h-5 text-text-secondary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
          <input
            ref="inputRef"
            :value="query"
            @input="onInput"
            type="text"
            placeholder="搜索待办、纪念日、旅行、日记…"
            class="flex-1 bg-transparent text-text-primary text-sm outline-none placeholder:text-text-tertiary"
          />
          <kbd class="text-xs text-text-secondary bg-background-primary px-xs rounded border border-border-subtle">Esc</kbd>
        </div>

        <!-- Results -->
        <div class="max-h-[50vh] overflow-y-auto">
          <div v-if="!query" class="px-md py-lg text-text-tertiary text-sm text-center">
            输入关键词搜索
          </div>
          <div v-else-if="results.length === 0" class="px-md py-lg text-text-tertiary text-sm text-center">
            没有找到结果
          </div>
          <ul v-else class="py-sm">
            <li
              v-for="(item, i) in results"
              :key="item.id"
              class="flex items-center gap-sm px-md py-sm cursor-pointer transition-colors"
              :class="i === selectedIndex ? 'bg-brand-primary/10' : 'hover:bg-background-secondary'"
              @click="selectResult(item)"
              @mouseenter="selectedIndex = i"
            >
              <component
                v-if="getModuleIcon(item.type === 'todo' ? 'todo' : item.type === 'anniversary' ? 'anniversaries' : item.type === 'travel' ? 'calendar' : 'diary')"
                :is="getModuleIcon(item.type === 'todo' ? 'todo' : item.type === 'anniversary' ? 'anniversaries' : item.type === 'travel' ? 'calendar' : 'diary')"
                class="w-4 h-4 text-text-secondary flex-shrink-0"
              />
              <div class="flex-1 min-w-0">
                <p class="text-text-primary text-sm truncate">{{ item.title }}</p>
                <p class="text-text-tertiary text-xs truncate">{{ item.subtitle }}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </Teleport>
</template>
